import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { breedingFoalNamesAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import RowActionsMenu from './RowActionsMenu';
import ConfirmDialog from './ConfirmDialog';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';

// Registry cap on a registered name (AQHA allows 20 characters including spaces).
// The column itself is VARCHAR(100), so this is the business rule, not a storage
// limit — change it here and in backend/routes/breeding_foal_names.py together.
const NAME_MAX_LENGTH = 20;

const EMPTY_FORM = { name: '', suggested_by: '', notes: '', is_favorite: false };

const BreedingFoalNames = ({ breedingRecordId }) => {
  const notify = useNotify();
  const [foalNames, setFoalNames] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFoalName, setEditingFoalName] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadFoalNames = useCallback(async () => {
    try {
      const response = await breedingFoalNamesAPI.getAll(breedingRecordId);
      setFoalNames(toArray(response.data, 'foal names'));
    } catch (error) {
      console.error('Error loading foal names:', error);
    }
  }, [breedingRecordId]);

  useEffect(() => {
    loadFoalNames();
  }, [loadFoalNames]);

  const handleOpenDialog = useCallback((foalName = null) => {
    if (foalName) {
      setEditingFoalName(foalName);
      setFormData({
        name: foalName.name,
        suggested_by: foalName.suggested_by || '',
        notes: foalName.notes || '',
        is_favorite: !!foalName.is_favorite
      });
    } else {
      setEditingFoalName(null);
      setFormData(EMPTY_FORM);
    }
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFoalName(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingFoalName) {
        await breedingFoalNamesAPI.update(breedingRecordId, editingFoalName.id, formData);
      } else {
        await breedingFoalNamesAPI.create(breedingRecordId, formData);
      }
      loadFoalNames();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving foal name:', error);
      notify.error(`Error saving foal name: ${errorMessage(error, error.message)}`);
    }
  };

  const handleToggleFavorite = async (foalName) => {
    try {
      await breedingFoalNamesAPI.update(breedingRecordId, foalName.id, {
        is_favorite: !foalName.is_favorite
      });
      loadFoalNames();
    } catch (error) {
      console.error('Error updating foal name:', error);
      notify.error(errorMessage(error, 'Error updating foal name'));
    }
  };

  const handleDeleteClick = (foalNameId) => setDeleteTarget(foalNameId);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await breedingFoalNamesAPI.delete(breedingRecordId, deleteTarget);
      setDeleteTarget(null);
      loadFoalNames();
    } catch (error) {
      console.error('Error deleting foal name:', error);
      notify.error(errorMessage(error, 'Error deleting foal name'));
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Possible Foal Names</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null)}
          >
            Add Name
          </Button>
        </Box>

        {foalNames.length === 0 ? (
          <Typography color="textSecondary">No foal names recorded</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Suggested By</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {foalNames.map((foalName) => (
                  <TableRow key={foalName.id}>
                    <TableCell align="center" sx={actionsKabobCellSx}>
                      <RowActionsMenu
                        actions={[
                          {
                            key: 'favorite',
                            label: foalName.is_favorite ? 'Unmark Favorite' : 'Mark Favorite',
                            icon: foalName.is_favorite
                              ? <StarBorderIcon fontSize="small" />
                              : <StarIcon fontSize="small" />,
                            onClick: () => handleToggleFavorite(foalName),
                          },
                          { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(foalName) },
                          { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(foalName.id), color: 'error' },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {foalName.is_favorite && (
                          <StarIcon fontSize="small" color="warning" titleAccess="Favorite" />
                        )}
                        <strong>{foalName.name}</strong>
                      </Box>
                    </TableCell>
                    <TableCell>{foalName.suggested_by || '-'}</TableCell>
                    <TableCell>{foalName.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Foal Name Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingFoalName ? 'Edit Foal Name' : 'Add Foal Name'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                inputProps={{ maxLength: NAME_MAX_LENGTH }}
                helperText={`${formData.name.length} / ${NAME_MAX_LENGTH} characters`}
                FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
              />

              <TextField
                size="small"
                label="Suggested By"
                value={formData.suggested_by}
                onChange={(e) => setFormData({ ...formData, suggested_by: e.target.value })}
                fullWidth
              />

              <TextField
                size="small"
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_favorite}
                    onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
                  />
                }
                label="Favorite"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.name.trim()}
            >
              {editingFoalName ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Foal Name"
        message="Permanently delete this foal name? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Card>
  );
};

export default BreedingFoalNames;
